import React from 'react';
import { Link } from 'react-router-dom';
import { Languages, ArrowLeft } from 'lucide-react';

const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen bg-background noise-overlay">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-border">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-primary/20 flex items-center justify-center">
              <Languages className="w-5 h-5 text-primary" />
            </div>
            <span className="font-heading text-xl font-semibold">
              <span className="text-primary/70">Language</span><span className="text-primary">Constructor</span>
            </span>
          </Link>
          <Link 
            to="/" 
            className="flex items-center gap-2 text-foreground-muted hover:text-foreground transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back</span>
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-16 px-6">
        <div className="max-w-3xl mx-auto">
          <h1 className="font-heading text-4xl font-bold text-foreground mb-2">Privacy Policy</h1>
          <p className="text-foreground-muted mb-8">Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>

          <div className="prose prose-invert max-w-none space-y-8">
            <section className="bg-background-paper border border-border rounded-lg p-6">
              <h2 className="font-heading text-xl font-semibold text-foreground mb-4">1. Introduction</h2>
              <p className="text-foreground-muted leading-relaxed">
                Welcome to LanguageConstructor. We respect your privacy and are committed to protecting 
                your personal data. This privacy policy explains how we collect, use, and safeguard your 
                information when you use our platform.
              </p>
            </section>

            <section className="bg-background-paper border border-border rounded-lg p-6">
              <h2 className="font-heading text-xl font-semibold text-foreground mb-4">2. Information We Collect</h2>
              <div className="space-y-4 text-foreground-muted leading-relaxed">
                <div>
                  <h3 className="text-foreground font-medium mb-2">Account Information</h3>
                  <ul className="list-disc list-inside space-y-1 ml-2">
                    <li>Email address</li>
                    <li>Display name</li>
                    <li>Profile photo (if provided)</li>
                    <li>Password (encrypted)</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-foreground font-medium mb-2">Content You Create</h3>
                  <ul className="list-disc list-inside space-y-1 ml-2">
                    <li>Constructed languages and their components (phonology, lexicon, grammar, etc.)</li>
                    <li>Text samples and translations</li>
                    <li>Any other content you add to your languages</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-foreground font-medium mb-2">Usage Data</h3>
                  <ul className="list-disc list-inside space-y-1 ml-2">
                    <li>Login timestamps</li>
                    <li>Feature usage patterns</li>
                    <li>Browser and device information</li>
                  </ul>
                </div>
              </div>
            </section>

            <section className="bg-background-paper border border-border rounded-lg p-6">
              <h2 className="font-heading text-xl font-semibold text-foreground mb-4">3. How We Use Your Information</h2>
              <ul className="list-disc list-inside space-y-2 text-foreground-muted leading-relaxed ml-2">
                <li>To provide and maintain the LanguageConstructor service</li>
                <li>To authenticate your account and ensure security</li>
                <li>To enable sharing features when you choose to make content public</li>
                <li>To send password reset emails and important service notifications</li>
                <li>To improve our platform based on usage patterns</li>
                <li>To provide customer support</li>
              </ul>
            </section>

            <section className="bg-background-paper border border-border rounded-lg p-6">
              <h2 className="font-heading text-xl font-semibold text-foreground mb-4">4. Content Visibility</h2>
              <div className="space-y-4 text-foreground-muted leading-relaxed">
                <p>
                  <strong className="text-foreground">Public Languages:</strong> When you set a language to "public," 
                  it becomes visible to all users on the Discover page and can be viewed by anyone.
                </p>
                <p>
                  <strong className="text-foreground">Private Languages:</strong> Languages marked as "private" are 
                  only visible to you and anyone you explicitly share them with.
                </p>
                <p>
                  <strong className="text-foreground">Shared Languages:</strong> When you share a language with specific 
                  users, only those users can access it.
                </p>
              </div>
            </section>

            <section className="bg-background-paper border border-border rounded-lg p-6 border-primary/30">
              <h2 className="font-heading text-xl font-semibold text-foreground mb-4">5. Administrative Access</h2>
              <div className="space-y-4 text-foreground-muted leading-relaxed">
                <p>
                  Platform administrators may access user accounts and content for the following purposes:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-2">
                  <li><strong className="text-foreground">Moderation:</strong> To review content that has been reported or flagged</li>
                  <li><strong className="text-foreground">Support:</strong> To assist users with technical issues</li>
                  <li><strong className="text-foreground">Platform Integrity:</strong> To investigate potential abuse or violations</li>
                  <li><strong className="text-foreground">Analytics:</strong> To understand aggregate usage patterns (anonymized)</li>
                </ul>
                <p className="mt-4 p-3 bg-primary/10 rounded-lg text-foreground">
                  <strong>Note:</strong> Administrators have read-only access to your content. They cannot 
                  edit, modify, or delete your languages or personal data without your explicit request.
                </p>
              </div>
            </section>

            <section className="bg-background-paper border border-border rounded-lg p-6">
              <h2 className="font-heading text-xl font-semibold text-foreground mb-4">6. Data Security</h2>
              <div className="space-y-4 text-foreground-muted leading-relaxed">
                <p>We implement appropriate security measures to protect your data:</p>
                <ul className="list-disc list-inside space-y-2 ml-2">
                  <li>Passwords are encrypted using industry-standard hashing</li>
                  <li>All data transmission uses HTTPS encryption</li>
                  <li>Regular security audits and updates</li>
                  <li>Access controls limiting who can view sensitive data</li>
                </ul>
              </div>
            </section>

            <section className="bg-background-paper border border-border rounded-lg p-6">
              <h2 className="font-heading text-xl font-semibold text-foreground mb-4">7. Third-Party Services</h2>
              <div className="space-y-4 text-foreground-muted leading-relaxed">
                <p>We use the following third-party services:</p>
                <ul className="list-disc list-inside space-y-2 ml-2">
                  <li><strong className="text-foreground">Google OAuth:</strong> For optional sign-in (if you choose this method)</li>
                  <li><strong className="text-foreground">Resend:</strong> For sending password reset and notification emails</li>
                </ul>
                <p>These services have their own privacy policies governing their use of your data.</p>
              </div>
            </section>

            <section className="bg-background-paper border border-border rounded-lg p-6">
              <h2 className="font-heading text-xl font-semibold text-foreground mb-4">8. Your Rights</h2>
              <div className="space-y-4 text-foreground-muted leading-relaxed">
                <p>You have the right to:</p>
                <ul className="list-disc list-inside space-y-2 ml-2">
                  <li>Access your personal data</li>
                  <li>Correct inaccurate data</li>
                  <li>Delete your account and associated data</li>
                  <li>Export your language data</li>
                  <li>Change your content visibility settings at any time</li>
                </ul>
                <p>To exercise these rights, please contact us through the platform or via email.</p>
              </div>
            </section>

            <section className="bg-background-paper border border-border rounded-lg p-6">
              <h2 className="font-heading text-xl font-semibold text-foreground mb-4">9. Data Retention</h2>
              <p className="text-foreground-muted leading-relaxed">
                We retain your data for as long as your account is active. If you delete your account, 
                we will delete your personal data and content within 30 days, except where we are required 
                to retain it for legal purposes.
              </p>
            </section>

            <section className="bg-background-paper border border-border rounded-lg p-6">
              <h2 className="font-heading text-xl font-semibold text-foreground mb-4">10. Changes to This Policy</h2>
              <p className="text-foreground-muted leading-relaxed">
                We may update this privacy policy from time to time. We will notify you of any significant 
                changes by posting the new policy on this page and updating the "Last updated" date.
              </p>
            </section>

            <section className="bg-background-paper border border-border rounded-lg p-6">
              <h2 className="font-heading text-xl font-semibold text-foreground mb-4">11. Contact Us</h2>
              <p className="text-foreground-muted leading-relaxed">
                If you have any questions about this privacy policy or our data practices, please contact 
                the site administrator through the platform.
              </p>
            </section>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-border py-8 px-6">
        <div className="max-w-4xl mx-auto flex items-center justify-between text-foreground-muted text-sm">
          <p>© {new Date().getFullYear()} LanguageConstructor</p>
          <div className="flex gap-6">
            <Link to="/about" className="hover:text-foreground transition-colors">About</Link>
            <Link to="/privacy" className="hover:text-foreground transition-colors">Privacy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicyPage;
